import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, Clock, ArrowLeft, ArrowRight, Home, BookOpen, Share2, User, Download, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import ExitIntentPopup from '../components/ExitIntentPopup';
import Glp1ExitPopup from '../components/Glp1ExitPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchRelatedPosts();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utm_source = urlParams.get('utm_source') || '';
      const utm_medium = urlParams.get('utm_medium') || '';
      const utm_campaign = urlParams.get('utm_campaign') || '';
      
      // Build URL with UTM params for tracking
      let url = `${API_URL}/api/blog/posts/${slug}`;
      const params = new URLSearchParams();
      if (utm_source) params.append('utm_source', utm_source);
      if (utm_medium) params.append('utm_medium', utm_medium);
      if (utm_campaign) params.append('utm_campaign', utm_campaign);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Article not found');
        }
        throw new Error('Failed to fetch article');
      }
      const data = await response.json();
      setPost(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts`);
      if (response.ok) {
        const data = await response.json();
        // Filter out current post and take first 3
        setRelatedPosts(data.filter(p => p.slug !== slug).slice(0, 3));
      }
    } catch (err) {
      // Related posts fetch is non-critical
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handleShare = async () => {
    const shareData = {
      title: post?.title,
      text: post?.excerpt,
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // Share cancelled by user
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Custom markdown components for styling
  const markdownComponents = {
    h1: ({ children }) => (
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-12 mb-6 leading-tight">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-10 mb-4 leading-tight">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-heading text-xl sm:text-2xl font-semibold text-white mt-8 mb-3 leading-tight">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-heading text-lg sm:text-xl font-semibold text-white mt-6 mb-2">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <div className="my-10 py-6 px-8 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-2xl text-center">
        <p className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white leading-snug">
          {children}
        </p>
      </div>
    ),
    p: ({ children }) => (
      <p className="text-slate-300 text-lg sm:text-xl leading-relaxed mb-6">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-none space-y-3 mb-6 ml-2">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside space-y-3 mb-6 text-slate-300 text-lg leading-relaxed">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-slate-300 text-lg leading-relaxed flex items-start gap-3">
        <span className="text-cyan-400 mt-1.5">•</span>
        <span>{children}</span>
      </li>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-cyan-500/50 pl-6 py-2 my-8 bg-cyan-500/5 rounded-r-lg">
        <div className="text-slate-300 text-lg italic">{children}</div>
      </blockquote>
    ),
    a: ({ href, children }) => {
      const text = typeof children === 'string' ? children : '';
      const isCtaButton = href && (
        text.includes('Assessment') || 
        text.includes('Forecast') || 
        text.includes('Build My') ||
        text.includes('Take the')
      );
      if (isCtaButton) {
        const isNad = href.includes('nad');
        return (
          <a
            href={href}
            className={`inline-flex items-center justify-center rounded-full px-8 py-4 text-lg font-bold text-white no-underline transition-all shadow-lg hover:shadow-xl ${
              isNad 
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 shadow-purple-500/30 hover:shadow-purple-500/50' 
                : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 shadow-emerald-500/30 hover:shadow-emerald-500/50'
            }`}
            data-testid="blog-cta-button"
          >
            {children}
          </a>
        );
      }
      return (
        <a 
          href={href} 
          className="text-cyan-400 hover:text-cyan-300 underline underline-offset-4 decoration-cyan-500/30 hover:decoration-cyan-400 transition-colors"
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    },
    strong: ({ children }) => (
      <strong className="font-semibold text-white">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-slate-200">{children}</em>
    ),
    code: ({ children }) => (
      <code className="bg-slate-800 text-cyan-400 px-2 py-1 rounded text-base font-mono">
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre className="bg-slate-900 border border-slate-800 rounded-xl p-4 overflow-x-auto my-6">
        {children}
      </pre>
    ),
    hr: () => (
      <hr className="border-t border-slate-700 my-12" />
    ),
    img: ({ src, alt }) => (
      <figure className="my-8">
        <img src={src} alt={alt} className="rounded-xl w-full" />
        {alt && <figcaption className="text-center text-slate-500 text-sm mt-3">{alt}</figcaption>}
      </figure>
    ),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 mb-6 rounded-2xl bg-slate-800/50 border border-slate-700 flex items-center justify-center">
          <BookOpen className="h-10 w-10 text-slate-500" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
        <p className="text-slate-400 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <div className="flex gap-4">
          <Button
            onClick={() => navigate('/blog')}
            variant="outline"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full px-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-full px-6"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post?.meta_title || post?.title}</title>
        <meta name="description" content={post?.meta_description || post?.excerpt} />
        <meta property="og:title" content={post?.meta_title || post?.title} />
        <meta property="og:description" content={post?.meta_description || post?.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {post?.featured_image && <meta property="og:image" content={post.featured_image} />}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="blog-post-page">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-500/50 transition-all">
                <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
              </div>
              <span className="font-heading text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">Bryan 2.0</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">Blog</span>
              </Link>
              <Link to="/about" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">About</span>
              </Link>
              <Link to="/free-starter-kit" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Free Kit</span>
              </Link>
              <Link to="/optimize" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Optimize</span>
              </Link>
              <Button
                onClick={() => navigate('/quiz')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-6 py-2 text-sm font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all"
              >
                Get My Forecast
              </Button>
            </nav>
          </div>
        </header>

        {/* Article */}
        <article className="py-12 sm:py-20 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Back Link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              <Link 
                to="/blog" 
                className="inline-flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all articles
              </Link>
            </motion.div>

            {/* Article Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {formatDate(post?.published_at)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {estimateReadTime(post?.content)}
                </span>
                <button 
                  onClick={handleShare}
                  className="flex items-center gap-1.5 hover:text-cyan-400 transition-colors ml-auto"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              </div>

              {/* Title */}
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {post?.title}
              </h1>

              {/* Excerpt */}
              <p className="text-xl sm:text-2xl text-slate-400 leading-relaxed">
                {post?.excerpt}
              </p>
            </motion.header>

            {/* Featured Image */}
            {post?.featured_image && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-12"
              >
                <img 
                  src={post.featured_image} 
                  alt={post.title} 
                  className="w-full rounded-2xl"
                />
              </motion.div>
            )}

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="prose prose-invert prose-lg max-w-none"
              data-testid="blog-post-content"
            >
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {post?.content}
              </ReactMarkdown>
            </motion.div>

            {/* GLP-1 Starter Kit Banner — shows on GLP-1 posts */}
            {(() => {
              const text = `${post?.title} ${post?.slug}`.toLowerCase();
              const isGlp1 = ['ozempic', 'semaglutide', 'tirzepatide', 'wegovy', 'glp-1', 'glp1'].some(k => text.includes(k));
              if (!isGlp1) return null;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="mt-12 p-6 sm:p-8 rounded-2xl bg-[#0B1F3A] border border-[#2ABFBF]/20 flex flex-col sm:flex-row items-center gap-6"
                  data-testid="blog-glp1-starter-kit-banner"
                >
                  <div className="flex-1">
                    <h4 className="text-lg sm:text-xl font-bold text-white mb-2">Want the Full GLP-1 Starter Kit?</h4>
                    <p className="text-[#C8E8E8] text-sm">Free 11-page guide covering medication comparison, meal plan, tracker, and more.</p>
                  </div>
                  <Button
                    onClick={() => navigate('/free-starter-kit')}
                    className="bg-[#2ABFBF] hover:bg-[#24a8a8] text-[#0B1F3A] rounded-full px-8 py-5 text-base font-bold whitespace-nowrap transition-colors flex-shrink-0"
                    data-testid="blog-starter-kit-btn"
                  >
                    Download Free →
                  </Button>
                </motion.div>
              );
            })()}

            {/* Compare CTA — shows when a relevant comparison exists */}
            {(() => {
              const slugLower = post?.slug?.toLowerCase() || '';
              const comparisons = [
                { match: ['mots-c', 'motsc', 'nad-vs', 'tirzepatide-nad'], slug: 'nad-vs-mots-c', label: 'MOTS-c or NAD+? See the difference', left: 'MOTS-c', right: 'NAD+' },
                { match: ['semaglutide-vs-tirzepatide', 'semaglutide', 'tirzepatide-weight'], slug: 'semaglutide-vs-tirzepatide', label: 'Semaglutide or Tirzepatide? Compare them', left: 'Semaglutide', right: 'Tirzepatide' },
                { match: ['sermorelin', 'tesamorelin'], slug: 'sermorelin-vs-tesamorelin', label: 'Sermorelin or Tesamorelin? See how they differ', left: 'Sermorelin', right: 'Tesamorelin' },
                { match: ['bpc-157', 'bpc157', 'wolverine', 'tb-500', 'tb500'], slug: 'bpc-157-vs-wolverine', label: 'BPC-157 or Wolverine Stack? Compare them', left: 'BPC-157', right: 'Wolverine' },
                { match: ['glutathione', 'nad-vs-nmn', 'nad-anti-aging'], slug: 'nad-vs-glutathione', label: 'NAD+ or Glutathione? See the difference', left: 'NAD+', right: 'Glutathione' },
              ];
              const match = comparisons.find(c => c.match.some(m => slugLower.includes(m)));
              if (!match) return null;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.32 }}
                  className="mt-10 p-5 sm:p-6 rounded-2xl bg-slate-800/30 border border-cyan-500/15 flex flex-col sm:flex-row items-center gap-4"
                  data-testid="blog-compare-cta"
                >
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-base font-semibold text-white mb-1">{match.label}</p>
                    <p className="text-sm text-slate-400">Side-by-side comparison in plain English.</p>
                  </div>
                  <Button
                    onClick={() => navigate(`/compare/${match.slug}`)}
                    className="bg-slate-700 hover:bg-slate-600 text-white rounded-full px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0"
                    data-testid="blog-compare-btn"
                  >
                    Compare {match.left} vs {match.right} →
                  </Button>
                </motion.div>
              );
            })()}

            {/* Article Footer CTA - Different for NAD+ and Sermorelin articles */}
            {(() => {
              const titleLower = post?.title?.toLowerCase() || '';
              const slugLower = post?.slug?.toLowerCase() || '';
              const isSermorelin = titleLower.includes('sermorelin') || slugLower.includes('sermorelin');
              const isNadArticle = titleLower.includes('nad') || slugLower.includes('nad');
              
              if (isSermorelin) {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-16 p-8 sm:p-10 backdrop-blur-xl bg-gradient-to-br from-purple-500/10 to-violet-500/5 border border-purple-500/20 rounded-2xl text-center"
                    data-testid="blog-post-sermorelin-cta"
                  >
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
                      Curious If Sermorelin Is Right for You?
                    </h3>
                    <p className="text-slate-400 mb-6 max-w-lg mx-auto">
                      Take our free 4-step Sermorelin assessment and find out if you're a good candidate. Or text Bryan directly — he personally responds to every inquiry.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                      <Button
                        onClick={() => navigate('/sermorelin-quiz')}
                        className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-400 hover:to-violet-400 text-white rounded-full px-10 py-6 text-lg font-bold shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] transition-all"
                      >
                        Take the Sermorelin Assessment →
                      </Button>
                      <a
                        href="sms:+18016518363"
                        className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                      >
                        Or text Bryan now → 801-651-8363
                      </a>
                    </div>
                  </motion.div>
                );
              }

              if (isNadArticle) {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-16 p-8 sm:p-10 backdrop-blur-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/5 border border-purple-500/20 rounded-2xl text-center"
                  >
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
                      Curious If NAD+ Is Right for You?
                    </h3>
                    <p className="text-slate-400 mb-6 max-w-lg mx-auto">
                      Take our free 2-minute NAD+ assessment and find out if you're a good candidate. Bryan will personally review your results.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                      <Button
                        onClick={() => navigate('/nad-quiz')}
                        className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 text-white rounded-full px-10 py-6 text-lg font-bold shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] transition-all"
                        data-testid="blog-post-nad-cta"
                      >
                        Take the NAD+ Assessment →
                      </Button>
                      <a
                        href="https://calendly.com/bryanm8623/30min"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                      >
                        Or book a free call with Bryan
                      </a>
                    </div>
                  </motion.div>
                );
              }
              
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-16 p-8 sm:p-10 backdrop-blur-xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/20 rounded-2xl text-center"
                >
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
                    Ready to Start Your Journey?
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-lg mx-auto">
                    Get your personalized AI weight loss forecast and discover which approach is right for you.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Button
                      onClick={() => navigate('/quiz')}
                      className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-10 py-6 text-lg font-bold shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_40px_rgba(16,185,129,0.6)] transition-all"
                      data-testid="blog-post-cta"
                    >
                      Get My AI Forecast
                    </Button>
                    <a
                      href="https://calendly.com/bryanm8623/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                    >
                      Or book a free call
                    </a>
                  </div>
                </motion.div>
              );
            })()}
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-16 px-4 sm:px-6 border-t border-slate-800/50">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
                More Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost, index) => (
                  <motion.article
                    key={relatedPost.id || relatedPost.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group"
                  >
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <div className="h-full backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-cyan-500/30 hover:shadow-[0_0_20px_rgba(0,240,255,0.1)] transition-all duration-300">
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                          <Calendar className="h-3 w-3" />
                          {formatDate(relatedPost.published_at)}
                        </div>
                        <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-slate-400 text-sm line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="border-t border-slate-800/50 py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Bryan 2.0 Forecast. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
              <Link to="/quiz" className="hover:text-cyan-400 transition-colors">Take Quiz</Link>
            </div>
          </div>
        </footer>
      {['ozempic', 'semaglutide', 'tirzepatide', 'glp-1', 'glp1', 'weight-loss'].some(k => slug?.toLowerCase().includes(k))
        ? <Glp1ExitPopup slug={slug} />
        : <ExitIntentPopup />
      }
      </div>
    </>
  );
};

export default BlogPost;
